import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Target, Users, Award, TrendingUp, Clock, ChevronLeft, ChevronRight, Star, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { useContactPopup } from '@/hooks/useContactPopup';

// Testimonial Card Component
const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const AboutPage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { openPopup } = useContactPopup();

  // Testimonials from HomePage
  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Founder & CEO, TechFlow Solutions',
      content: 'Trademark Assist made trademark registration effortless. Their team handled everything professionally, from the initial search to final approval. We received our certificate in just 5 months—much faster than expected. The comprehensive search report gave us complete confidence before filing.',
      rating: 5
    },
    {
      name: 'James Robertson',
      role: 'Creative Director, Pixel & Canvas Studio',
      content: 'As a creative agency, protecting our original work is paramount. Trademark Assist registered our copyrights efficiently and explained the entire process clearly. Their expertise in IP law is evident, and their pricing is remarkably transparent.',
      rating: 4.5
    },
    {
      name: 'Priya Sharma',
      role: 'Marketing Director, GreenLeaf Organics',
      content: 'Before launching our rebrand, we needed certainty that our new name was available. Trademark Assist\'s comprehensive search uncovered a potential conflict we\'d completely missed. They saved us from a costly legal nightmare and helped us pivot to a stronger brand name.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Co-Founder, UrbanBuild Construction Ltd',
      content: 'Starting a business is overwhelming, but Trademark Assist made company formation seamless. They handled all the paperwork, ensured compliance with Companies House, and delivered our incorporation certificate within 24 hours. Outstanding service from start to finish.',
      rating: 4
    },
    {
      name: 'Emma Thompson',
      role: 'E-commerce Manager, Heritage Home Goods',
      content: 'Getting into Amazon Brand Registry seemed impossible until we found Trademark Assist. They guided us through trademark registration specifically for Amazon\'s requirements, then handled the Brand Registry enrollment process. Our sales increased 40% thanks to the added protection.',
      rating: 5
    },
    {
      name: 'David Williams',
      role: 'Managing Director, Elite Fashion Group',
      content: 'We had a complex international trademark portfolio that needed management. Trademark Assist provided expert guidance on multi-jurisdiction filings and helped us navigate the complexities of EU and UK trademark law post-Brexit. Their attention to detail is unmatched.',
      rating: 4.5
    },
    {
      name: 'Aisha Khan',
      role: 'Founder, TechStartup Hub',
      content: 'The team at Trademark Assist went above and beyond to expedite our trademark application when we were facing a tight deadline. Their proactive communication and expert handling meant we launched on schedule with full legal protection.',
      rating: 5
    },
    {
      name: 'Oliver Martinez',
      role: 'Brand Manager, Luxury Watches UK',
      content: 'After a trademark dispute, we needed immediate legal support. Trademark Assist responded within hours and handled the entire opposition process masterfully. Their strategic approach saved our brand identity and prevented significant financial loss.',
      rating: 4.5
    },
    {
      name: 'Sophie Anderson',
      role: 'CEO, Digital Marketing Solutions',
      content: 'I appreciated the transparency and clear communication throughout our trademark registration. Trademark Assist explained every step in simple terms and kept us informed of progress. The pricing was fair and there were no hidden fees.',
      rating: 4
    },
    {
      name: 'Raj Patel',
      role: 'Founder, GreenTech Innovations',
      content: 'Trademark Assist has been our trusted partner for over three years. They\'ve successfully registered multiple trademarks for our expanding product line and provided invaluable advice on brand protection strategy. Their expertise has been instrumental in our growth.',
      rating: 5
    }
  ];

  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(testimonialInterval);
  }, [currentTestimonial]);

  const coreValues = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Trust & Integrity',
      description: 'We build lasting relationships with our clients through transparent practices, honest advice, and unwavering commitment to your success. Every interaction is guided by integrity and professionalism.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Excellence in Service',
      description: 'We maintain the highest standards in every trademark registration, search, and consultation we handle. Our 98% success rate is testament to our meticulous attention to detail and expert knowledge.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Client-Focused Approach',
      description: 'Your success is our success. We tailor our services to meet your unique business needs, providing personalized guidance and support throughout your intellectual property journey.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Deep Expertise',
      description: 'Our team brings over 15 years of combined experience in UK trademark law, intellectual property protection, and business strategy. We stay current with IPO regulations to serve you better.'
    }
  ];

  const whyChooseUs = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Proven Track Record',
      description: 'With over 10,000 trademarks successfully registered and a 98% approval rate, we have the results that speak for themselves. Our clients consistently achieve trademark protection on their first application attempt.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Expert Legal Team',
      description: 'Our trademark attorneys and IP specialists have navigated thousands of registrations, disputes, and complex scenarios. Their deep understanding of UK trademark law ensures your application succeeds.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: '24/7 Support',
      description: 'We\'re with you every step of the way, from initial consultation to final registration and beyond. Our round-the-clock support means you\'re never left wondering about application status or next steps.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Comprehensive Protection',
      description: 'We don\'t just file paperwork—we build protection strategies. Our services include thorough searches, strategic classification advice, and guidance on strengthening your intellectual property portfolio.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-4 bg-primary text-primary-foreground">UK's Most Trusted Trademark Service Since 2009</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              About Trademark Assist
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Protecting businesses and brands across the United Kingdom with professional trademark registration, intellectual property services, and expert legal guidance since 2009.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About the Company */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                About Trademark Assist
              </h2>
              <div className="space-y-4 text-lg text-muted-foreground leading-relaxed flex-1">
                <p>
                  Founded in 2009, Trademark Assist has grown to become one of the UK's most trusted trademark registration and intellectual property protection services. We've successfully registered over 10,000 trademarks for businesses across every industry, from startups and sole traders to established enterprises and international corporations.
                </p>
                <p>
                  Our journey began with a simple mission: make professional trademark protection accessible to all UK businesses, regardless of size or budget. We recognized that many small businesses and entrepreneurs were either navigating trademark registration alone (risking costly mistakes) or paying premium prices for basic services.
                </p>
                <p>
                  Today, we're proud to offer comprehensive trademark services at transparent, affordable prices—without compromising on quality or expertise. Our team of trademark attorneys and IP specialists brings over 15 years of combined experience, achieving an exceptional 98% approval rate on trademark applications.
                </p>
                <p>
                  We serve clients throughout England, Scotland, Wales, and Northern Ireland, handling everything from initial trademark searches and UK IPO filings to company formation, copyright registration, and Amazon Brand Registry enrollment. Our client-focused approach means you receive personalized guidance tailored to your specific business needs and goals.
                </p>
                
                {/* CTA Button */}
                <div className="pt-4">
                  <Button 
                    onClick={openPopup}
                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
                    size="lg"
                  >
                    Get Started Today
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Right: Image - Full Height */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-full min-h-[550px]"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-elegant-xl h-full">
                <img
                  src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=900&fit=crop"
                  alt="Professional Trademark Services Team"
                  className="w-full h-full object-cover"
                />
                {/* Decorative overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent"></div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-full blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision and Mission */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Our Vision & Mission
              </h2>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-8 h-full border-2 border-primary hover:shadow-elegant-xl transition-all duration-300 bg-gradient-to-br from-primary/5 to-background">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
                    <Target className="w-8 h-8 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Our Vision</h3>
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  To be the UK's most trusted and accessible trademark protection service, empowering every business—from solo entrepreneurs to growing enterprises—to secure and protect their brand identity with confidence. We envision a future where trademark protection is never a barrier to business success, but a foundation for growth and innovation.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-8 h-full border-2 border-secondary hover:shadow-elegant-xl transition-all duration-300 bg-gradient-to-br from-secondary/5 to-background">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary to-primary rounded-xl flex items-center justify-center shadow-lg">
                    <Award className="w-8 h-8 text-secondary-foreground" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Our Mission</h3>
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  To deliver exceptional trademark registration and intellectual property services through expert guidance, transparent pricing, and unwavering client support. We combine legal precision with business understanding, ensuring your trademark registration isn't just successful—it's strategic. Our commitment is to make professional IP protection accessible, affordable, and stress-free for all UK businesses.
                </p>
              </Card>
            </motion.div>
          </div>

          {/* CTA Button for Vision & Mission Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-primary hover:bg-primary/90 text-primary-foreground group"
              size="lg"
            >
              Protect Your Brand Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Core Values */}
          <div className="text-center mb-12 mt-20">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">Our Core Values</h3>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              These principles guide everything we do, from client interactions to trademark application preparation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 h-full border-border hover:border-secondary transition-all duration-300 group relative overflow-hidden">
                  {/* Red fill effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-secondary/10 transition-all duration-300"></div>
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-secondary/10 to-secondary/20 group-hover:from-secondary group-hover:to-secondary-light rounded-lg flex items-center justify-center text-secondary group-hover:text-secondary-foreground mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      {value.icon}
                    </div>
                    <h4 className="text-lg font-semibold text-foreground mb-2 group-hover:text-secondary transition-colors duration-300">{value.title}</h4>
                    <p className="text-muted-foreground leading-relaxed text-sm">{value.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button for Core Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Start Your Trademark Journey
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Choose Trademark Assist
              </h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                In the crowded marketplace of trademark services, we stand apart through proven expertise, unwavering client commitment, and results that speak for themselves. Our team combines legal precision with business understanding, ensuring your trademark registration isn't just successful—it's strategic.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyChooseUs.map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 border-2 border-transparent hover:border-primary transition-all duration-500 border-border h-full group relative overflow-hidden bg-gradient-to-br from-background to-accent/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/20 group-hover:from-primary group-hover:to-primary-light group-hover:shadow-glow rounded-xl flex items-center justify-center text-primary group-hover:text-primary-foreground mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                      {reason.icon}
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                      {reason.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button for Why Choose Us Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Start Your Registration
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What Our Clients Say About Us
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Don't just take our word for it—hear from the businesses we've helped protect and grow.
            </p>
          </div>

          <div className="relative">
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                  {getVisibleTestimonials().map((testimonial, index) => (
                    <div key={`${testimonial.originalIndex}-${index}`}>
                      <TestimonialCard testimonial={testimonial} />
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-2 rounded-full transition-all duration-500 ease-out ${
                    Math.floor(currentTestimonial / 3) === index
                      ? 'bg-primary w-8 shadow-glow' 
                      : 'bg-border w-2 hover:bg-primary/50 hover:w-4'
                  }`}
                  aria-label={`View testimonial group ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground">
              Common questions about our services and trademark registration process
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                How long does the trademark registration process take?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                The typical trademark registration timeline in the UK is 4-6 months from application submission to receiving your certificate. This includes the initial examination by the UK IPO (1-2 weeks), publication in the Trade Marks Journal (2-month opposition period), and final registration. We monitor your application throughout and handle any IPO queries immediately to avoid delays. Complex cases or oppositions can extend this timeline, but we keep you informed at every stage.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                What makes your service different from DIY trademark registration?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                While DIY filing is possible, our service provides comprehensive pre-filing searches that identify conflicts DIY applicants miss, expert classification advice (critical for protection scope), proper application preparation that meets IPO standards, and handling of office actions that confuse most non-lawyers. Over 40% of DIY applications are rejected—wasting government fees and requiring restart. Our 98% success rate and expert guidance eliminate these risks, making our service a wise investment in your brand's future.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                Do I need separate trademarks for different products or services?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                It depends on your business activities. Trademarks are registered for specific classes of goods or services (45 classes total). If you sell clothing (Class 25) and plan to open retail stores (Class 35), you should register in both classes. During consultation, we analyze your current and planned business activities to determine the appropriate classes. Registering in additional classes now is more cost-effective than separate applications later and provides broader protection against competitors in adjacent industries.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                What happens if someone opposes my trademark application?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                If someone files an opposition during the 2-month publication period, we first assess the strength of their opposition and your position. Many oppositions are based on weak grounds and can be overcome with proper response. We'll present strategic options: defending with full legal arguments, negotiating a coexistence agreement, or modifying your application. Opposition defense is included in our Premium package or available as an additional service for Basic/Standard packages. Our experience means we know how to position your case for the best outcome.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                Can you help with international trademark registration?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Yes, we assist with international trademark protection through several routes: the Madrid Protocol (file in multiple countries through one application), EUIPO registration (covers all EU member states), or direct filings in specific countries. Each approach has different costs and strategic considerations based on where you do business and your expansion plans. We assess your international needs and recommend the most cost-effective protection strategy. Many clients start with UK protection and expand internationally as their business grows.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          
          {/* CTA Button for FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-primary hover:bg-primary/90 text-primary-foreground group"
              size="lg"
            >
              Get Expert Guidance
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
